import type { Room } from "@/types/Room";

// A room's `amenities` is typed as `Array<Amenity | []>`, so guard each element before reading name.
function amenityNamesOf(room: Room): string[] {
  const list = (room.amenities ?? []) as Array<{ name?: string } | unknown>;
  return list
    .map((a) => (a && typeof a === "object" && "name" in a ? (a as { name?: string }).name : undefined))
    .filter((n): n is string => typeof n === "string" && n.length > 0);
}

/** Distinct, sorted set of amenity names present across the given rooms — the filter's options. */
export function distinctRoomAmenityNames(rooms: Room[]): string[] {
  const names = new Set<string>();
  for (const room of rooms) {
    for (const name of amenityNamesOf(room)) names.add(name);
  }
  return [...names].sort((a, b) => a.localeCompare(b));
}

/**
 * True when the room has EVERY selected amenity (AND semantics). An empty selection matches all
 * rooms (no filter applied). Matched by amenity NAME, since each room holds its own amenity
 * instance with a distinct id.
 */
export function roomHasAllAmenities(room: Room, selectedNames: string[]): boolean {
  if (selectedNames.length === 0) return true;
  const roomNames = new Set(amenityNamesOf(room));
  return selectedNames.every((n) => roomNames.has(n));
}

/** Narrow a room list to those matching ALL selected amenities (empty selection = unchanged). */
export function filterRoomsByAmenities(rooms: Room[], selectedNames: string[]): Room[] {
  if (selectedNames.length === 0) return rooms;
  return rooms.filter((room) => roomHasAllAmenities(room, selectedNames));
}
